import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { CatalogTranslationsFilters } from './interface';
import {
  getCatalogTranslationsAsync,
  createCatalogTranslationAsync,
  updateCatalogTranslationAsync,
  deleteCatalogTranslationAsync,
  refreshCatalogTranslationsCacheAsync,
} from './asyncActions';

export const catalogTranslationsSlice = createSlice({
  name: 'catalogTranslations',
  initialState,
  reducers: {
    setFilters: (state, { payload }: PayloadAction<Partial<CatalogTranslationsFilters>>) => {
      state.filters = { ...state.filters, ...payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // list
      .addCase(getCatalogTranslationsAsync.pending, (state) => {
        state.list.status = 'loading';
      })
      .addCase(getCatalogTranslationsAsync.fulfilled, (state, action) => {
        state.list.status = 'idle';
        // Backend returns SimpleApiResponse<MultipleResults<T>>:
        //   response.data.item  = { items: DTO[], total: N }
        state.list.data = action.payload?.item?.items ?? [];
      })
      .addCase(getCatalogTranslationsAsync.rejected, (state) => {
        state.list.status = 'failed';
      })
      // create
      .addCase(createCatalogTranslationAsync.pending, (state) => {
        state.save.status = 'loading';
      })
      .addCase(createCatalogTranslationAsync.fulfilled, (state, action) => {
        state.save.status = 'idle';
        if (action.payload?.item) {
          state.list.data = [...state.list.data, action.payload.item];
        }
      })
      .addCase(createCatalogTranslationAsync.rejected, (state) => {
        state.save.status = 'failed';
      })
      // update
      .addCase(updateCatalogTranslationAsync.pending, (state) => {
        state.save.status = 'loading';
      })
      .addCase(updateCatalogTranslationAsync.fulfilled, (state, action) => {
        state.save.status = 'idle';
        if (action.payload?.item) {
          const updated = action.payload.item;
          state.list.data = state.list.data.map((item) => (item.id === updated.id ? updated : item));
        }
      })
      .addCase(updateCatalogTranslationAsync.rejected, (state) => {
        state.save.status = 'failed';
      })
      // delete
      .addCase(deleteCatalogTranslationAsync.pending, (state) => {
        state.delete.status = 'loading';
      })
      .addCase(deleteCatalogTranslationAsync.fulfilled, (state, action) => {
        state.delete.status = 'idle';
        // id was the arg passed to the thunk
        const id = action.meta.arg;
        state.list.data = state.list.data.filter((item) => item.id !== id);
      })
      .addCase(deleteCatalogTranslationAsync.rejected, (state) => {
        state.delete.status = 'failed';
      })
      // refresh cache
      .addCase(refreshCatalogTranslationsCacheAsync.pending, (state) => {
        state.refreshCache.status = 'loading';
      })
      .addCase(refreshCatalogTranslationsCacheAsync.fulfilled, (state) => {
        state.refreshCache.status = 'idle';
      })
      .addCase(refreshCatalogTranslationsCacheAsync.rejected, (state) => {
        state.refreshCache.status = 'failed';
      });
  },
});

export const { setFilters, resetFilters } = catalogTranslationsSlice.actions;

export default catalogTranslationsSlice.reducer;
